import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangleIcon, GavelIcon, TrendingUpIcon } from 'lucide-react';
import {
    HistorySearchBox,
    HistorySearchBoxReturnStateType,
    SEARCH_ANY_STRING,
    availableSearchTypes,
} from './HistorySearchBox';
import HistoryTable from './HistoryTable';
import { HistoryStatsResp, HistoryTableSearchType } from '@shared/historyApiTypes';
import { useBackendApi } from '@/hooks/fetch';

const HistorySearchBoxMemo = memo(HistorySearchBox);
const HistoryTableMemo = memo(HistoryTable);

const updateUrlSearchParams = (
    search: HistoryTableSearchType,
    filterbyType: string | undefined,
    filterbyAdmin: string | undefined,
) => {
    const newUrl = new URL(window.location.toString());
    if (search?.type && search?.value) {
        newUrl.searchParams.set('searchType', search.type);
        newUrl.searchParams.set('searchQuery', search.value);
    } else {
        newUrl.searchParams.delete('searchType');
        newUrl.searchParams.delete('searchQuery');
    }
    if (filterbyType && filterbyType !== '!any') {
        newUrl.searchParams.set('filterbyType', filterbyType);
    } else {
        newUrl.searchParams.delete('filterbyType');
    }
    if (filterbyAdmin && filterbyAdmin !== '!any') {
        newUrl.searchParams.set('filterbyAdmin', filterbyAdmin);
    } else {
        newUrl.searchParams.delete('filterbyAdmin');
    }
    window.history.replaceState({}, '', newUrl);
};

const getInitialState = () => {
    const params = new URLSearchParams(window.location.search);
    const validSearchTypes = availableSearchTypes.map(f => f.value) as string[];
    const searchType = params.get('searchType');
    const searchQuery = params.get('searchQuery');
    return {
        search: searchQuery && searchType && validSearchTypes.includes(searchType)
            ? { type: searchType, value: searchQuery }
            : { type: availableSearchTypes[0].value, value: '' },
        filterbyType: params.get('filterbyType') ?? SEARCH_ANY_STRING,
        filterbyAdmin: params.get('filterbyAdmin') ?? SEARCH_ANY_STRING,
    } satisfies HistorySearchBoxReturnStateType;
};

type StatCardProps = {
    label: string;
    value: number | false;
    icon: React.ElementType;
    accent: string;
    prefix?: string;
};

function StatCard({ label, value, icon: Icon, accent, prefix }: StatCardProps) {
    return (
        <div
            className="flex items-center gap-3 px-4 py-3 rounded-xl border bg-white/[0.01] border-white/[0.06] flex-1 min-w-[120px]"
            style={{ boxShadow: `0 0 20px ${accent}05` }}
        >
            <div
                className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border"
                style={{ background: `${accent}0c`, borderColor: `${accent}20` }}
            >
                <Icon size={14} style={{ color: accent }} />
            </div>
            <div className="min-w-0">
                <div className="text-[10px] font-mono font-bold uppercase tracking-widest text-slate-500 leading-none mb-1">
                    {label}
                </div>
                <div className="text-lg font-black font-mono text-slate-100 leading-none">
                    {value === false ? (
                        <span className="text-slate-600 text-sm">—</span>
                    ) : (
                        <>{prefix}<span style={{ color: accent }}>{value.toLocaleString()}</span></>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function HistoryPage() {
    const [calloutData, setCalloutData] = useState<HistoryStatsResp | undefined>(undefined);
    const [searchBoxReturn, setSearchBoxReturn] = useState<HistorySearchBoxReturnStateType | undefined>(undefined);
    const statsApi = useBackendApi<HistoryStatsResp>({
        method: 'GET',
        path: '/history/stats',
        abortOnUnmount: true,
    });

    useEffect(() => {
        statsApi({ success(data) { setCalloutData(data); } });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const doSearch = useCallback((
        search: HistoryTableSearchType,
        filterbyType: string | undefined,
        filterbyAdmin: string | undefined,
    ) => {
        setSearchBoxReturn({ search, filterbyType, filterbyAdmin });
        updateUrlSearchParams(search, filterbyType, filterbyAdmin);
    }, []);

    const initialState = useMemo(getInitialState, []);
    const hasStats = calloutData && !('error' in calloutData);

    return (
        <div className="flex flex-col gap-4 min-w-96 w-full h-contentvh">
            {/* Stat cards */}
            <div className="flex flex-wrap gap-3">
                <StatCard
                    label="Total Bans"
                    value={hasStats ? calloutData.totalBans : false}
                    icon={GavelIcon}
                    accent="#ef4444"
                />
                <StatCard
                    label="Bans Last 7d"
                    value={hasStats ? calloutData.bansLast7d : false}
                    icon={TrendingUpIcon}
                    accent="#ef4444"
                    prefix="+"
                />
                <StatCard
                    label="Total Warns"
                    value={hasStats ? calloutData.totalWarns : false}
                    icon={AlertTriangleIcon}
                    accent="#fbbf24"
                />
                <StatCard
                    label="Warns Last 7d"
                    value={hasStats ? calloutData.warnsLast7d : false}
                    icon={TrendingUpIcon}
                    accent="#fbbf24"
                    prefix="+"
                />
            </div>

            {/* Search + filters */}
            {calloutData && !('error' in calloutData) && (
                <HistorySearchBoxMemo
                    doSearch={doSearch}
                    initialState={initialState}
                    adminStats={calloutData.groupedByAdmins}
                />
            )}

            {/* Table */}
            {searchBoxReturn && (
                <HistoryTableMemo
                    search={searchBoxReturn.search}
                    filterbyType={searchBoxReturn.filterbyType}
                    filterbyAdmin={searchBoxReturn.filterbyAdmin}
                />
            )}
        </div>
    );
}
